import BiMap from 'bidirectional-map';

import { Block, type Document } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';
import { normalizeURLAnchor } from '@gitbook/util-url';

import * as expandable from '../expandable';
import {
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_TABS_ITEM,
} from '../types';
import { isSectionDelimiter, type SectionDelimiterBlock } from './isSectionDelimiter';

/**
 * Mapping of block keys to ID for url.
 */
export type SectionIDs = BiMap<string>;

// Cache to optimize multiple calls to getSectionIDs when rendering the application.
const sectionIdsCache = new WeakMap<Document | Block, SectionIDs>();

/**
 * Create an empty SectionIDs.
 */
export function createSectionIDs(): SectionIDs {
    return new BiMap<string>();
}

/**
 * Create a mapping of ID for headers (key => id). Pass an existing SectionIDs and, if there
 * are no changes, the same instance is returned.
 */
export function getSectionIDs(document: Document, existing: SectionIDs = new BiMap()): SectionIDs {
    const inCache = sectionIdsCache.get(document);
    if (inCache) {
        return inCache;
    }

    const ids = createSectionIDs();

    const allocateIdForNode = (node: SectionDelimiterBlock) => {
        const baseID = normalizeURLAnchor(getNodeTextForID(node));
        const ID = getNextAvailableID(ids, baseID);
        ids.set(node.key, ID);
    };

    const forEachNode = (nodes: List<Block>) => {
        nodes.forEach((node) => {
            if (!node.isBlock()) {
                return false;
            }

            if (isSectionDelimiter(node)) {
                allocateIdForNode(node);
            }

            if (Block.isBlock(node.nodes.first())) {
                forEachNode(node.blockNodes);
            }

            node.fragments.forEach((fragment) => {
                forEachNode(fragment.nodes);
            });
        });
    };

    forEachNode(document.nodes);

    // Re-use reference if there are no changes
    // it optimizes the rendering of the application
    const result = existing && areSectionIDsEqual(ids, existing) ? existing : ids;
    sectionIdsCache.set(document, result);

    return result;
}

/**
 * Helper for getSectionIDs, find the next available id if one aready taken
 */
function getNextAvailableID(sectionIDs: SectionIDs, baseID: string, i = 0): string {
    const ID = i > 0 ? `${baseID}-${i}` : baseID;
    const hasNodeWithSameID = sectionIDs.hasValue(ID);

    if (hasNodeWithSameID) {
        return getNextAvailableID(sectionIDs, baseID, i + 1);
    }

    return ID;
}

/**
 * Get the text to compute the best looking ID for a node.
 */
export function getNodeTextForID(node: SectionDelimiterBlock): string {
    switch (node.type) {
        case BLOCK_OPENAPI: {
            if (node.data.has('ref')) {
                return `${node.data.get('method').toUpperCase()} ${node.data.get('path')}`;
            }
            return node.data.get('summary') || '';
        }
        case BLOCK_OPENAPI_OPERATION:
            return `${node.data.get('method').toUpperCase()} ${node.data.get('path')}`;
        case BLOCK_OPENAPI_SCHEMAS: {
            const schemas = node.data.get('schemas');
            const schema = schemas[0];
            if (schemas.length === 1 && schema) {
                return `The ${schema} object`;
            }
            return '';
        }
        case BLOCK_OPENAPI_WEBHOOK: {
            return node.data.has('ref') ? node.data.get('name') : '';
        }
        case BLOCK_EXPANDABLE: {
            return expandable.getTitleFragment(node).text;
        }
        case BLOCK_TABS_ITEM: {
            return node.data.get('title') || '';
        }
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3:
            return node.data.get('id') || node.text || '';
        default:
            return '';
    }
}

/**
 * Returns true if the two given HeadingIDs are equal.
 */
function areSectionIDsEqual(lhs: SectionIDs, rhs: SectionIDs): boolean {
    if (lhs.size !== rhs.size) {
        return false;
    }

    for (const lhsKey of lhs.keys()) {
        if (rhs.get(lhsKey) !== lhs.get(lhsKey)) {
            return false;
        }
    }

    return true;
}
